import { convertToFloat, routeFileSelector } from '@/utilz';
import ScrollPagination from '@/views/components/Paginations/ScrollPagination';
import FooterContentCard from '@/views/screens/common/FooterContentCard';
import { Col, Divider, Row } from 'antd';
import { cloneDeep, maxBy } from 'es-toolkit';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  getLADClaimSettlementsInProgress,
  updateLossAndDamageClaimDetailsInProgress,
} from '../../../../../../redux/Claims/LossAndDamage/LossAndDamageClaimsActions';
import { LADCovertDatesFromMomentToAPIType } from '../../../../../../utilz/PreClaimsUtils';
import Button from '../../../../../components/Buttons/Button';
import PrimaryLabel from '../../../../../components/Labels/PrimaryLabel';
import LADSettlementCard from './LADSettlementCard';

const LADSettlementsTab = () => {
  const dispatch = useDispatch();
  const claimDetails = useSelector((state) => state.lossAndDamage.claimDetails);
  const claimSettlements = useSelector((state) => state.lossAndDamage.claimSettlements);
  const [settlementList, setSettlementList] = useState([]);
  const [pageIndex, setPageIndex] = useState(1);
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  useEffect(() => {
    if (claimSettlements.success && claimSettlements?.data) {
      if (claimSettlements.data.length > 0) {
        setSettlementList(
          claimSettlements.data.map((data) => ({ ...data, amount: convertToFloat(data.amount), isNew: false })),
        );
      } else if (settlementList.length === 0) {
        handleAddNew();
      }
    }
  }, [claimSettlements]);

  const setEditMode = (index, val) => {
    const settlementListLocal = cloneDeep(settlementList);
    settlementListLocal[index].editMode = val;
    setSettlementList(settlementListLocal);
  };

  const handleCancel = (index, isNew) => () => {
    const settlementListLocal = cloneDeep(settlementList);
    if (isNew) {
      settlementListLocal.splice(index, 1);
    } else {
      settlementListLocal[index] = {
        ...settlementListLocal[index],
        ...claimDetails?.data?.claimSettlement[index],
        editMode: false,
      };
    }
    setSettlementList(settlementListLocal);
  };

  const handleSubmit = (data, index) => {
    const handleSuccess = () => {
      setEditMode(index, false);
    };
    const claimDetailsLocal = cloneDeep(claimDetails.data);
    claimDetailsLocal.claimSettlement = [{ ...data, dirtyFlag: data?.isNew ? 'I' : 'U' }];
    const formData = LADCovertDatesFromMomentToAPIType(claimDetailsLocal);
    formData.handleSuccess = handleSuccess;
    formData.section = 'SETTLEMENTS';
    formData.pageIndex = pageIndex;
    dispatch(updateLossAndDamageClaimDetailsInProgress(formData));
  };

  const handleDelete = (index) => () => {
    setSettlementList((prevList) => prevList.filter((_, i) => i !== index));
    let claimDetailsLocal = cloneDeep(claimDetails.data);
    claimDetailsLocal = LADCovertDatesFromMomentToAPIType(claimDetailsLocal);
    claimDetailsLocal.claimSettlement = [{ ...settlementList[index], dirtyFlag: 'D' }];
    claimDetailsLocal.section = 'SETTLEMENTS';
    claimDetailsLocal.pageIndex = pageIndex;
    dispatch(updateLossAndDamageClaimDetailsInProgress(claimDetailsLocal));
  };

  const handleAddNew = () => {
    let settlementListLocal = cloneDeep(settlementList);
    let nextSequence = maxBy(settlementListLocal, ({ settlementSeq }) => Number(settlementSeq));
    nextSequence = nextSequence ? nextSequence.settlementSeq : 0;
    settlementListLocal = [
      {
        claimKey: claimDetails?.data?.claimKey,
        settlementSeq: nextSequence + 1,
        accountingCode: undefined,
        amount: undefined,
        dirtyFlag: 'i',
        isNew: true,
        editMode: true,
      },
      ...settlementListLocal,
    ];
    setSettlementList(settlementListLocal);
  };

  useEffect(() => {
    if (claimDetails.data?.claimKey) {
      dispatch(
        getLADClaimSettlementsInProgress({
          claimKey: claimDetails.data.claimKey,
          pageIndex,
        }),
      );
    }
  }, [claimDetails.data?.claimKey]);

  const handleNextPage = () => {
    if (claimDetails.data?.claimKey) {
      const nextPageNo = pageIndex + 1;
      dispatch(
        getLADClaimSettlementsInProgress({
          claimKey: claimDetails.data.claimKey,
          pageIndex: nextPageNo,
        }),
      );
      setPageIndex(nextPageNo);
    }
  };

  return (
    <div className="d-flex py-16 h-100" style={{ flexDirection: 'column' }}>
      <Row className="px-30 align-items-center" justify="space-between">
        <Col>
          <PrimaryLabel className="fw-700 f-18">Claim Settlements</PrimaryLabel>
        </Col>
        <Col>
          <Row justify="end" gutter={16}>
            <Col>
              <Button onClick={handleAddNew}>Add New Settlement</Button>
            </Col>
          </Row>
        </Col>
      </Row>
      <Divider className="mt-0 my-16 ml-n16 vw-100" />
      {settlementList.length > 0 || claimSettlements.progressing ? (
        <ScrollPagination
          className="px-30 py-10 flex-1"
          loadMore={handleNextPage}
          isLoading={claimSettlements.progressing}
          hasMore={claimSettlements.data?.length < claimSettlements.totalRows}
          scrollOffset={30}
        >
          {settlementList.map((row, index) => (
            <LADSettlementCard
              settlementDetails={row}
              key={row.settlementSeq}
              index={index}
              editMode={Boolean(row.editMode)}
              setEditMode={setEditMode}
              handleCancel={handleCancel}
              handleSubmit={handleSubmit}
              handleDelete={handleDelete}
            />
          ))}
        </ScrollPagination>
      ) : (
        <div className="flex-1" />
      )}
      <Row justify="start" className="footer-container background-header mx-30 py-10">
        <Col md={12} />
        <Col md={6} className="px-15">
          <FooterContentCard
            justify="start"
            count={convertToFloat(claimSettlements.totalAmount)}
            label={t('translation:claims.totalAmount')}
          />
        </Col>
      </Row>
    </div>
  );
};

export default LADSettlementsTab;
